import React, { useState } from 'react';
import { Button, Collapse, Badge } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faChevronDown, faChevronUp, faMobileAlt } from '@fortawesome/free-solid-svg-icons';

/**
 * AndroidAppFlavours Component
 * An expandable/collapsible section listing Android application editions and flavours
 * (HealthSync, HealthSync Pro, HealthyMe, HealthyMe Pro) as configured in libhacontimig.
 * Preserves screen real estate and ensures surrounding card element sizes remain aligned.
 */
const appEditions = [
  {
    name: 'HealthyMe Pro',
    tier: 'PRO',
    store: 'Google Play Store',
    badgeVariant: 'warning',
    pkg: 'com.linardsl.libhacontimig.hm.finalPro',
    url: 'https://play.google.com/store/apps/details?id=com.linardsl.libhacontimig.hm.finalPro',
    description: 'Flagship tier with on-chain Health Diagram NFT minting (Ethereum & Sui) and advanced Oura/Health Connect telemetry.'
  },
  {
    name: 'HealthyMe',
    tier: 'Standard',
    store: 'Google Play Store',
    badgeVariant: 'secondary',
    pkg: 'com.linardsl.libhacontimig.hm.final',
    url: 'https://play.google.com/store/apps/details?id=com.linardsl.libhacontimig.hm.final',
    description: 'Core health tracking and daily sleep/activity/readiness synchronization.'
  },
  {
    name: 'HealthSync Pro',
    tier: 'PRO',
    store: 'Huawei AppGallery / Galaxy Store',
    badgeVariant: 'warning',
    pkg: 'com.linardsl.libhacontimig.finalPro',
    url: 'https://appgallery.huawei.com/#/app/C1042',
    description: 'Huawei Health & Samsung Health bi-directional sync with dedicated PCD extensions.'
  },
  {
    name: 'HealthSync',
    tier: 'Standard',
    store: 'Huawei AppGallery / Galaxy Store',
    badgeVariant: 'secondary',
    pkg: 'com.linardsl.libhacontimig.final',
    url: 'https://appgallery.huawei.com/#/app/C1041',
    description: 'Baseline cross-platform health metrics synchronization.'
  }
];

const AndroidAppFlavours = ({ defaultOpen = false, className = '' }) => {
  const [isOpen, setIsOpen] = useState(defaultOpen);

  return (
    <div className={`android-app-flavours-wrapper my-2 ${className}`}>
      <Button
        variant="outline-secondary"
        size="sm"
        className="w-100 d-flex justify-content-between align-items-center py-1 px-2 border-dashed"
        onClick={() => setIsOpen(!isOpen)}
        aria-expanded={isOpen}
        aria-controls="android-flavours-collapse"
        style={{ fontSize: '0.8rem', borderRadius: '6px' }}
      >
        <span>
          <FontAwesomeIcon icon={faMobileAlt} className="me-1 text-warning" />
          Supported App Editions ({appEditions.length})
        </span>
        <FontAwesomeIcon icon={isOpen ? faChevronUp : faChevronDown} className="small text-muted" />
      </Button>

      <Collapse in={isOpen}>
        <div id="android-flavours-collapse" className="mt-2 text-start">
          <div className="card card-body bg-light border-0 p-2 shadow-sm rounded-2" style={{ fontSize: '0.78rem' }}>
            {appEditions.map((app) => (
              <div key={app.name} className="py-1 border-bottom border-light last-border-0">
                <div className="d-flex justify-content-between align-items-center">
                  <a
                    href={app.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="fw-bold text-dark text-decoration-none hover-underline"
                  >
                    {app.name}
                  </a>
                  <div>
                    <Badge bg={app.badgeVariant} text={app.tier === 'PRO' ? 'dark' : 'white'} className="me-1">
                      {app.tier}
                    </Badge>
                    <Badge bg="dark" className="small" style={{ fontSize: '0.65rem' }}>
                      {app.store.split(' ')[0]}
                    </Badge>
                  </div>
                </div>
                <div className="text-muted small mt-1" style={{ fontSize: '0.72rem', lineHeight: '1.2' }}>
                  {app.description}
                </div>
              </div>
            ))}
          </div>
        </div>
      </Collapse>
    </div>
  );
};

export default AndroidAppFlavours;

